import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { PowerStationPerformanceData } from '../types/DailyReportPerformanceChart.types';
import { resolveDailyReportDateTime, toDailyReportDayKey } from '../utils/DailyReportDateTime';

const API_URL = 'api/daily-report/performance-chart';

const fetchPerformanceChartData = async (
  selectedDate: string | null,
  powerStationIds?: number[] | null
): Promise<PowerStationPerformanceData[]> => {
  const resolvedDateTime = resolveDailyReportDateTime(selectedDate);
  if (!resolvedDateTime) {
    throw new Error('Data não selecionada');
  }

  try {
    const params = new URLSearchParams();
    params.append('selectedDate', resolvedDateTime);
    if (powerStationIds && powerStationIds.length > 0) {
      powerStationIds.forEach((id) => params.append('powerStationIds', String(id)));
    }

    const response = await axios.get<PowerStationPerformanceData[]>(
      API_URL,
      {
        params,
        timeout: 30000,
      }
    );

    return response.data || [];
  } catch (error) {
    console.error('Erro ao buscar dados de performance do gráfico:', error);
    return Promise.reject(error);
  }
};

export const useDailyReportPerformanceChartQuery = (
  selectedDate: string | null,
  powerStationIds?: number[] | null
) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const queryKey = ['daily-report-performance-chart', toDailyReportDayKey(selectedDate), powerStationIds];

  return useQuery({
    queryKey,
    queryFn: () => fetchPerformanceChartData(selectedDate, powerStationIds),
    enabled: !!selectedDate,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onError: (error: any) => {
      // Falha do refresh automático com dados já em tela: mantém o último valor sem repetir o aviso a cada ciclo.
      if (queryClient.getQueryData(queryKey) !== undefined) return;
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        'Erro ao carregar dados de performance das usinas';
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 3000,
      });
    },
  });
};
